import { useEffect, useState } from 'react'
import { BarChart3, TrendingUp, Eye, MousePointerClick, Clock, RefreshCw, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getAdvertiserAnalytics, listAds, type AdvertiserAnalyticsResponse, type AdvertiserAdItem } from '@/lib/api/advertiser'

type Range = '7d' | '30d'

export function AdvertiserAnalytics() {
  const [data, setData] = useState<AdvertiserAnalyticsResponse | null>(null)
  const [ads, setAds] = useState<AdvertiserAdItem[]>([])
  const [range, setRange] = useState<Range>('7d')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [analyticsRes, adsRes] = await Promise.all([
        getAdvertiserAnalytics({ range }),
        listAds({ limit: 200 }),
      ])
      setData(analyticsRes)
      setAds(adsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range])

  const adName = (adId: string) => ads.find((a) => a.adId === adId)?.name ?? adId

  const fmt = (n: number) => n.toLocaleString()
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ad analytics</h1>
          <p className="mt-1 text-sm text-gray-400">
            Impression tracking, completion rates, and reach metrics for your campaigns.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['7d', '30d'] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                range === r
                  ? 'bg-[#D72638]/10 border-[#D72638]/40 text-[#D72638]'
                  : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
              }`}
            >
              {r === '7d' ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-gray-700 text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-yellow-700 bg-yellow-900/20 px-4 py-3 flex items-start gap-3 text-sm text-yellow-300">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {error} — analytics data will be available once the ad-serving backend is live and
            impressions have been recorded.
          </span>
        </div>
      )}

      {/* KPI cards */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Total impressions"
          value={loading ? '—' : fmt(data?.totalImpressions ?? 0)}
          sub={`${range === '7d' ? 'Last 7 days' : 'Last 30 days'}`}
          icon={<Eye className="w-4 h-4 text-[#D72638]" />}
        />
        <MetricCard
          label="Completion rate"
          value={loading ? '—' : pct(data?.overallCompletionRate ?? 0)}
          sub="% of viewers who watched ≥ 80%"
          icon={<TrendingUp className="w-4 h-4 text-[#D72638]" />}
        />
        <MetricCard
          label="Click-through rate"
          value={loading ? '—' : pct(data?.overallCtr ?? 0)}
          sub="Clicks ÷ impressions"
          icon={<MousePointerClick className="w-4 h-4 text-[#D72638]" />}
        />
        <MetricCard
          label="Total completions"
          value={loading ? '—' : fmt(data?.totalCompletions ?? 0)}
          sub={`${fmt(data?.totalClicks ?? 0)} total clicks`}
          icon={<Clock className="w-4 h-4 text-[#D72638]" />}
        />
      </section>

      {/* Per-ad breakdown */}
      {!loading && data && data.byAd && data.byAd.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
            Performance by ad creative
          </h2>
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-900/50">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                    Ad creative
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium">
                    Impressions
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                    Completions
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                    Completion %
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium hidden lg:table-cell">
                    Clicks
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium hidden lg:table-cell">
                    CTR
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.byAd.map((row) => (
                  <tr
                    key={row.adId}
                    className="border-b border-gray-900 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-white truncate max-w-[200px]">
                        {adName(row.adId)}
                      </p>
                      <p className="text-xs text-gray-500">{row.adId.slice(0, 8)}…</p>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300">{fmt(row.totalImpressions)}</td>
                    <td className="px-4 py-3 text-right text-gray-300 hidden md:table-cell">
                      {fmt(row.completions)}
                    </td>
                    <td className="px-4 py-3 text-right hidden md:table-cell">
                      <CompletionBar value={row.completionRate} />
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 hidden lg:table-cell">
                      {fmt(row.clicks)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 hidden lg:table-cell">
                      {pct(row.clickThroughRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Empty state when no impressions yet */}
      {!loading && (!data || !data.byAd || data.byAd.length === 0) && !error && (
        <div className="rounded-xl border border-dashed border-gray-800 p-12 flex flex-col items-center gap-4 text-center">
          <BarChart3 className="w-10 h-10 text-gray-700" />
          <div>
            <p className="text-sm font-medium text-gray-400">No impression data yet</p>
            <p className="text-xs text-gray-600 mt-1 max-w-xs">
              Impressions will appear here once ads are assigned to films and viewers start
              watching content with ad placements.
            </p>
          </div>
        </div>
      )}

      {/* What will be tracked info box */}
      <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/60 to-black p-5 text-sm text-gray-400 space-y-2">
        <p className="font-medium text-white">What is tracked</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-xs leading-relaxed">
          <p><strong className="text-gray-300">Impressions</strong> — unique ad views per placement</p>
          <p><strong className="text-gray-300">Completion rate</strong> — % who watched ≥ 80%</p>
          <p><strong className="text-gray-300">Click-through rate</strong> — clicks ÷ impressions</p>
          <p><strong className="text-gray-300">Reach by film</strong> — which films drive most exposure</p>
          <p><strong className="text-gray-300">Device breakdown</strong> — mobile vs. web split</p>
          <p><strong className="text-gray-300">Weekly reports</strong> — automated SES email to sponsors</p>
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string
  value: string
  sub: string
  icon: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        {icon}
      </div>
      <p className="text-2xl font-semibold text-white mb-1">{value}</p>
      <p className="text-xs text-gray-400">{sub}</p>
    </div>
  )
}

function CompletionBar({ value }: { value: number }) {
  const pct = Math.min(Math.max(value * 100, 0), 100)
  const color = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-16 h-1.5 rounded-full bg-gray-800 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-300 w-10 text-right">{pct.toFixed(1)}%</span>
    </div>
  )
}
